import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { state, addMealToSlot } from '../store/store';
import { addDays, formatWeekRange } from '../lib/dates';
import type { MealType } from '../types';

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];

type SlotState =
  | { kind: 'empty' }
  | { kind: 'same' }
  | { kind: 'other'; name: string };
// 'same' = the recipe being added is already one of the entries in this slot.
// 'other' = the slot has at least one recipe but none of them is this one.

export function AddToPlanModal(props: {
  recipeId: string;
  open: boolean;
  initialWeekId: string;
  onClose: () => void;
}) {
  const s = state.value!;
  const [weekId, setWeekId] = useState(props.initialWeekId);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  // Reset to initial week each time the modal re-opens
  useEffect(() => {
    if (props.open) setWeekId(props.initialWeekId);
  }, [props.open, props.initialWeekId]);

  // Escape closes
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.open]);

  // Scroll-lock, focus first element, restore focus on close
  useEffect(() => {
    if (!props.open) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    queueMicrotask(() => {
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      previousActiveRef.current?.focus();
    };
  }, [props.open]);

  function trapTab(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  if (!props.open) return null;

  const enabled: MealType[] = s.settings.meal_types_enabled;
  const dates = Array.from({ length: 5 }, (_, i) => addDays(weekId, i));
  const week = s.weeks.find(w => w.id === weekId);
  const recipe = s.recipes.find(r => r.id === props.recipeId);

  function slotState(date: string, mt: MealType): SlotState {
    const slotMeals = (week?.meals ?? []).filter(m =>
      m.date === date && m.meal_type === mt && m.recipe_id
    );
    if (slotMeals.length === 0) return { kind: 'empty' };
    if (slotMeals.some(m => m.recipe_id === props.recipeId)) return { kind: 'same' };
    // Show the first existing entry's name as the cell hint; user gets to add
    // alongside it (no confirm — multiple meals per slot is supported now).
    const first = slotMeals[0]!;
    const other = s.recipes.find(r => r.id === first.recipe_id);
    return { kind: 'other', name: other?.name ?? 'Unknown' };
  }

  async function handleClick(date: string, mt: MealType) {
    const st = slotState(date, mt);
    if (st.kind === 'same') return;
    await addMealToSlot(weekId, date, mt, props.recipeId);
    props.onClose();
  }

  function nextWeek() { setWeekId(addDays(weekId, 7)); }
  function prevWeek() { setWeekId(addDays(weekId, -7)); }

  return (
    <div
      className="add-to-plan-modal"
      onClick={() => props.onClose()}
    >
      <div
        ref={dialogRef}
        className="add-to-plan-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="addtoplan-title"
        onKeyDown={trapTab}
        onClick={e => e.stopPropagation()}
      >
        <div className="add-to-plan-header">
          <strong id="addtoplan-title" style={{ fontSize: 16 }}>Add "{recipe?.name ?? 'recipe'}" to plan</strong>
          <button
            type="button"
            aria-label="Close"
            onClick={() => props.onClose()}
            className="add-to-plan-close"
          >×</button>
        </div>

        <div className="add-to-plan-week-nav">
          <button type="button" onClick={prevWeek} aria-label="Previous week" className="add-to-plan-nav-btn">←</button>
          <span className="add-to-plan-week-label">{formatWeekRange(weekId)}</span>
          <button type="button" onClick={nextWeek} aria-label="Next week" className="add-to-plan-nav-btn">→</button>
        </div>

        <div
          className="add-to-plan-grid"
          style={{ gridTemplateColumns: `60px repeat(5, 1fr)` }}
        >
          <div />
          {dates.map((d, i) => (
            <div key={d} className="add-to-plan-day-head">
              <div style={{ fontWeight: 700, fontSize: 12 }}>{DAY_LABELS[i]}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{d.slice(5).replace('-', '/')}</div>
            </div>
          ))}

          {enabled.map(mt => (
            <Fragment key={`row-${mt}`}>
              <div className="label-caps add-to-plan-mt-label">{mt}</div>
              {dates.map((d, di) => {
                const st = slotState(d, mt);
                const dayName = (DAY_LABELS[di] ?? '').toLowerCase();
                return (
                  <button
                    key={`${mt}-${d}`}
                    type="button"
                    aria-label={`Add ${mt} on ${dayName}`}
                    onClick={() => handleClick(d, mt)}
                    disabled={st.kind === 'same'}
                    className={`add-to-plan-cell add-to-plan-cell-${st.kind}`}
                  >
                    {st.kind === 'empty' && <span>+ add</span>}
                    {st.kind === 'same' && <span>✓ on plan</span>}
                    {st.kind === 'other' && <span>{st.name.length > 18 ? st.name.slice(0, 18) + '…' : st.name}</span>}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
